import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { useCookies } from 'react-cookie';
import axios from 'axios';
import StoreContext from '../components/Store/context';
import { API_BASE_URL } from '../utils/useApi';
import {
  parseNumber,
  calculateLineItem,
  evaluatePromotions,
  calculateCartTotals,
} from '../utils/posCartEngine';
import { buildSearchItemIndex } from '../utils/posFuzzyEngine';
import { resolveScaleBarcodeMatch } from '../utils/scaleBarcodeParser';

const POSContext = createContext({});

const getInitialTheme = (cookieValue) => {
  if (cookieValue === 'dark' || cookieValue === 'light') {
    return cookieValue;
  }
  if (
    typeof window !== 'undefined' &&
    window.matchMedia &&
    window.matchMedia('(prefers-color-scheme: dark)').matches
  ) {
    return 'dark';
  }
  return 'light';
};

export const POSProvider = ({ children }) => {
  const { credencials } = useContext(StoreContext);
  const [cookies, setCookie] = useCookies(['duzz_pdv_theme']);

  // Gerenciamento de Tema (Modo Escuro / Claro) exclusivo do PDV
  const [themeMode, setThemeMode] = useState(() => getInitialTheme(cookies.duzz_pdv_theme));

  const toggleTheme = useCallback(() => {
    setThemeMode((prev) => {
      const nextTheme = prev === 'dark' ? 'light' : 'dark';
      setCookie('duzz_pdv_theme', nextTheme, {
        path: '/',
        maxAge: 60 * 60 * 24 * 365, // 1 ano
      });
      return nextTheme;
    });
  }, [setCookie]);

  const isDarkMode = themeMode === 'dark';

  // Status de carregamento e sincronização
  const [loadingCatalog, setLoadingCatalog] = useState(true);
  const [syncingCatalog, setSyncingCatalog] = useState(false);
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [catalogError, setCatalogError] = useState(null);

  // Dados brutos do catálogo
  const [products, setProducts] = useState([]);
  const [services, setServices] = useState([]);
  const [productCategories, setProductCategories] = useState([]);
  const [serviceCategories, setServiceCategories] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [frequentCustomers, setFrequentCustomers] = useState([]);

  // Estado do Carrinho Local
  const [cartItems, setCartItems] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedPromotion, setSelectedPromotion] = useState(null);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [manualDiscount, setManualDiscountState] = useState(0);
  const [manualDiscountType, setManualDiscountType] = useState('money'); // 'money' | 'percent'
  const [observation, setObservation] = useState('');
  const [plots, setPlots] = useState(1);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState(null);

  // Fullscreen helper
  const [isFullScreen, setIsFullScreen] = useState(Boolean(document.fullscreenElement));

  useEffect(() => {
    const handleFsChange = () => {
      setIsFullScreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', handleFsChange);
    return () => document.removeEventListener('fullscreenchange', handleFsChange);
  }, []);

  const toggleFullScreen = useCallback(() => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch((err) => {
        console.warn('Fullscreen request rejected:', err);
      });
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch((err) => console.warn(err));
      }
    }
  }, []);

  // Caixa ativo da sessão
  const [activeCashier, setActiveCashier] = useState(null);
  const [loadingCashier, setLoadingCashier] = useState(false);

  // Headers de autenticação
  const authHeaders = useMemo(() => ({
    sessionToken: credencials?.sessionToken,
    company: credencials?.company ? String(credencials.company) : '',
  }), [credencials]);

  // Carregar dados do caixa da sessão
  const loadActiveCashier = useCallback(async () => {
    if (!credencials?.cashSession || !credencials?.sessionToken) {
      setActiveCashier(null);
      return null;
    }
    setLoadingCashier(true);
    try {
      const res = await axios.get(`${API_BASE_URL}/cash-registers`, {
        params: { id: credencials.cashSession },
        headers: authHeaders,
      });
      if (res.data && res.data[0]) {
        setActiveCashier(res.data[0]);
        return res.data[0];
      }
      setActiveCashier(null);
      return null;
    } catch (err) {
      console.error('Erro ao carregar caixa da sessão no PDV:', err);
      return null;
    } finally {
      setLoadingCashier(false);
    }
  }, [credencials?.cashSession, credencials?.sessionToken, authHeaders]);

  useEffect(() => {
    loadActiveCashier();
  }, [loadActiveCashier]);

  // Preload / Sincronização do catálogo
  const syncCatalog = useCallback(async (isBackground = false) => {
    if (!credencials?.sessionToken) return;

    if (isBackground) {
      setSyncingCatalog(true);
    } else {
      setLoadingCatalog(true);
    }
    setCatalogError(null);

    try {
      const [
        prodRes,
        servRes,
        promoRes,
        payRes,
        custRes,
        prodCatRes,
        servCatRes,
      ] = await Promise.all([
        axios.get(`${API_BASE_URL}/products`, { headers: authHeaders, params: { lacking: 0 } }).catch(() => ({ data: [] })),
        axios.get(`${API_BASE_URL}/services`, { headers: authHeaders, params: { isActive: 1 } }).catch(() => ({ data: [] })),
        axios.get(`${API_BASE_URL}/promotions`, { headers: authHeaders, params: { isActive: 1, isExpired: 0 } }).catch(() => ({ data: [] })),
        axios.get(`${API_BASE_URL}/payments/methods`, { headers: authHeaders }).catch(() => ({ data: [] })),
        axios.get(`${API_BASE_URL}/customers`, { headers: authHeaders }).catch(() => ({ data: [] })),
        axios.get(`${API_BASE_URL}/products/categories`, { headers: authHeaders, params: { isActive: 1 } }).catch(() => ({ data: [] })),
        axios.get(`${API_BASE_URL}/services/categories`, { headers: authHeaders, params: { isActive: 1 } }).catch(() => ({ data: [] })),
      ]);

      const loadedProducts = Array.isArray(prodRes.data) ? prodRes.data : [];
      const loadedServices = Array.isArray(servRes.data) ? servRes.data : [];
      const loadedPromotions = Array.isArray(promoRes.data) ? promoRes.data : [];
      const loadedPayments = Array.isArray(payRes.data) ? payRes.data : [];
      const loadedCustomers = Array.isArray(custRes.data) ? custRes.data : [];
      const loadedProdCats = Array.isArray(prodCatRes.data) ? prodCatRes.data : [];
      const loadedServCats = Array.isArray(servCatRes.data) ? servCatRes.data : [];

      setProducts(loadedProducts);
      setServices(loadedServices);
      setPromotions(loadedPromotions);
      setPaymentMethods(loadedPayments);
      setFrequentCustomers(loadedCustomers);
      setProductCategories(loadedProdCats);
      setServiceCategories(loadedServCats);
      setLastSyncTime(new Date());

      // Atualiza os preços dos itens que já estão no carrinho se houve alteração no servidor
      setCartItems((prevItems) => {
        if (!prevItems || prevItems.length === 0) return prevItems;
        return prevItems.map((item) => {
          if (item.typeItem === 'product') {
            const found = loadedProducts.find((p) => String(p.id) === String(item.id));
            if (found && parseNumber(found.value) !== item.valueUn) {
              const newValUn = parseNumber(found.value, item.valueUn);
              return calculateLineItem({
                ...item,
                valueUn: newValUn,
              });
            }
          } else if (item.typeItem === 'service') {
            const found = loadedServices.find((s) => String(s.id) === String(item.id));
            if (found && parseNumber(found.value) !== item.valueUn) {
              const newValUn = parseNumber(found.value, item.valueUn);
              return calculateLineItem({
                ...item,
                valueUn: newValUn,
              });
            }
          }
          return item;
        });
      });

      // Salva snapshot leve no localStorage para carregamento imediato
      try {
        localStorage.setItem('pos_catalog_products', JSON.stringify(loadedProducts));
        localStorage.setItem('pos_catalog_payments', JSON.stringify(loadedPayments));
        localStorage.setItem('pos_catalog_prod_categories', JSON.stringify(loadedProdCats));
        localStorage.setItem('pos_catalog_serv_categories', JSON.stringify(loadedServCats));
      } catch (e) {
        // ignora se passar do limite de quota do localStorage
      }
    } catch (err) {
      console.error('Falha ao carregar catálogo para o PDV:', err);
      setCatalogError('Falha ao sincronizar dados com o servidor.');
    } finally {
      setLoadingCatalog(false);
      setSyncingCatalog(false);
    }
  }, [credencials, authHeaders]);

  // Carga inicial
  useEffect(() => {
    // Tenta carregar do storage rápido primeiro para render imediato
    try {
      const cachedProds = localStorage.getItem('pos_catalog_products');
      const cachedPays = localStorage.getItem('pos_catalog_payments');
      const cachedProdCats = localStorage.getItem('pos_catalog_prod_categories');
      const cachedServCats = localStorage.getItem('pos_catalog_serv_categories');
      if (cachedProds) setProducts(JSON.parse(cachedProds));
      if (cachedPays) setPaymentMethods(JSON.parse(cachedPays));
      if (cachedProdCats) setProductCategories(JSON.parse(cachedProdCats));
      if (cachedServCats) setServiceCategories(JSON.parse(cachedServCats));
    } catch (e) {
      // noop
    }

    syncCatalog(false);
  }, [syncCatalog]);

  // Mapas Indexados O(1) para busca em alta velocidade
  const productsByBarcode = useMemo(() => {
    const map = new Map();
    products.forEach((p) => {
      if (p.barCode) {
        map.set(String(p.barCode).trim().toLowerCase(), p);
      }
      if (p.id) {
        map.set(String(p.id), p);
      }
    });
    return map;
  }, [products]);

  const servicesByBarcode = useMemo(() => {
    const map = new Map();
    services.forEach((s) => {
      if (s.barCode) {
        map.set(String(s.barCode).trim().toLowerCase(), s);
      }
      if (s.id) {
        map.set(String(s.id), s);
      }
    });
    return map;
  }, [services]);

  // Mapa de resolução rápida de categorias (id -> nome)
  const categoriesMap = useMemo(() => {
    const map = new Map();
    productCategories.forEach((c) => {
      if (c && c.id !== undefined) map.set(String(c.id), c.name);
    });
    serviceCategories.forEach((c) => {
      if (c && c.id !== undefined) map.set(String(c.id), c.name);
    });
    return map;
  }, [productCategories, serviceCategories]);

  // Lista consolidada de categorias com contagem de produtos/serviços
  const categories = useMemo(() => {
    const list = [];
    const seenCatKeys = new Set();

    // Categorias de produtos cadastradas
    productCategories.forEach((cat) => {
      const catIdStr = String(cat.id);
      const count = products.filter((p) => {
        const pCatId = p.category && typeof p.category === 'object' && p.category.id !== undefined
          ? String(p.category.id)
          : (p.category !== null && p.category !== undefined ? String(p.category) : '');
        return pCatId === catIdStr;
      }).length;
      list.push({
        id: catIdStr,
        name: cat.name,
        type: 'product',
        count,
      });
      seenCatKeys.add(`product-${catIdStr}`);
    });

    // Categorias de serviços cadastradas
    serviceCategories.forEach((cat) => {
      const catIdStr = String(cat.id);
      const count = services.filter((s) => {
        const sCatId = s.category && typeof s.category === 'object' && s.category.id !== undefined
          ? String(s.category.id)
          : (s.category !== null && s.category !== undefined ? String(s.category) : '');
        return sCatId === catIdStr;
      }).length;
      list.push({
        id: catIdStr,
        name: cat.name,
        type: 'service',
        count,
      });
      seenCatKeys.add(`service-${catIdStr}`);
    });

    // Coleta também categorias embutidas nos próprios produtos caso não venham do endpoint de categorias
    products.forEach((p) => {
      if (p.category && typeof p.category === 'object' && p.category.id !== undefined && p.category.name) {
        const key = `product-${p.category.id}`;
        if (!seenCatKeys.has(key)) {
          seenCatKeys.add(key);
          const count = products.filter((item) => item.category?.id === p.category.id).length;
          list.push({
            id: String(p.category.id),
            name: p.category.name,
            type: 'product',
            count,
          });
        }
      }
    });

    return list;
  }, [productCategories, serviceCategories, products, services]);

  // Catálogo completo pré-indexado para busca fonética/fuzzy de alta performance no F2
  const indexedCatalog = useMemo(() => {
    const list = [
      ...products.map((p) => {
        const catId = p.category && typeof p.category === 'object' && p.category.id !== undefined
          ? String(p.category.id)
          : (p.category !== null && p.category !== undefined ? String(p.category) : '');
        const catName =
          (p.category && typeof p.category === 'object' && p.category.name) ||
          categoriesMap.get(catId) ||
          '';
        return {
          id: p.id,
          name: p.name,
          barCode: p.barCode,
          valueUn: parseFloat(p.value || 0),
          typeItem: 'product',
          category: p.category,
          categoryId: catId,
          categoryName: catName,
          amount: parseFloat(p.amount || 0),
          images: p.images,
        };
      }),
      ...services.map((s) => {
        const catId = s.category && typeof s.category === 'object' && s.category.id !== undefined
          ? String(s.category.id)
          : (s.category !== null && s.category !== undefined ? String(s.category) : '');
        const catName =
          (s.category && typeof s.category === 'object' && s.category.name) ||
          categoriesMap.get(catId) ||
          '';
        return {
          id: s.id,
          name: s.name,
          barCode: s.barCode,
          valueUn: parseFloat(s.value || 0),
          typeItem: 'service',
          category: s.category,
          categoryId: catId,
          categoryName: catName,
          amount: 999999,
          images: s.images,
        };
      }),
    ];
    return list.map(buildSearchItemIndex);
  }, [products, services, categoriesMap]);

  // Adicionar item genérico ao carrinho
  const addItem = useCallback((itemData, quantity = 1) => {
    setCartItems((prevItems) => {
      const existingIdx = prevItems.findIndex((it) => (
        it.id === itemData.id && it.typeItem === itemData.typeItem
      ));

      const parsedQty = Math.max(0.001, parseNumber(quantity, 1));

      if (existingIdx >= 0) {
        const updated = [...prevItems];
        const currentItem = updated[existingIdx];
        const newAmount = Math.round((parseNumber(currentItem.amount, 1) + parsedQty) * 10000) / 10000;
        updated[existingIdx] = calculateLineItem({
          ...currentItem,
          amount: newAmount,
        });
        return updated;
      }

      const newItem = calculateLineItem({
        ...itemData,
        amount: parsedQty,
        discount: 0,
        discountType: 'money',
      });
      return [...prevItems, newItem];
    });
  }, []);

  // Adicionar item por bipagem ou digitação de código/nome (com suporte a balança e fracionados)
  const addItemByBarcode = useCallback((barcodeInput, amountMultiplier = 1) => {
    if (!barcodeInput) return { success: false, message: 'Código vazio' };

    const cleanCode = String(barcodeInput).trim().toLowerCase();
    const qty = Math.max(0.001, parseNumber(amountMultiplier, 1));

    // 1. Busca exata por código de barras ou ID nos produtos
    const foundProduct = productsByBarcode.get(cleanCode);
    if (foundProduct) {
      addItem({
        id: foundProduct.id,
        name: foundProduct.name,
        valueUn: parseNumber(foundProduct.value, 0),
        barCode: foundProduct.barCode,
        typeItem: 'product',
        category: foundProduct.category,
        maxStock: parseNumber(foundProduct.amount, 999999),
        images: foundProduct.images,
      }, qty);
      return { success: true, item: foundProduct, amount: qty };
    }

    // 2. Busca exata por código de barras ou ID nos serviços
    const foundService = servicesByBarcode.get(cleanCode);
    if (foundService) {
      addItem({
        id: foundService.id,
        name: foundService.name,
        valueUn: parseNumber(foundService.value, 0),
        barCode: foundService.barCode,
        typeItem: 'service',
        category: foundService.category,
        maxStock: 999999,
        images: foundService.images,
      }, qty);
      return { success: true, item: foundService, amount: qty };
    }

    // 3. Detecção inteligente modular de código de balança (Toledo Preço Total + Fallback Legado por Peso)
    const findProductInCatalog = (plu, pluStr) => {
      const pluNumber = String(plu);
      return (
        productsByBarcode.get(pluStr) ||
        productsByBarcode.get(pluNumber) ||
        products.find(
          (p) =>
            String(p.id) === pluNumber ||
            String(p.id) === pluStr ||
            String(p.barCode) === pluStr ||
            String(p.barCode) === pluNumber ||
            (p.barCode && p.barCode.endsWith(pluStr))
        )
      );
    };

    const scaleMatch = resolveScaleBarcodeMatch(cleanCode, findProductInCatalog);
    if (scaleMatch) {
      const { product: matchedProd, quantity: scaleQty } = scaleMatch;
      const unitPrice = parseNumber(matchedProd.value, 0);
      const finalScaleQty = Math.round((scaleQty * qty) * 10000) / 10000;

      addItem(
        {
          id: matchedProd.id,
          name: matchedProd.name,
          valueUn: unitPrice,
          barCode: matchedProd.barCode,
          typeItem: 'product',
          category: matchedProd.category,
          maxStock: parseNumber(matchedProd.amount, 999999),
          images: matchedProd.images,
        },
        finalScaleQty
      );
      return { success: true, item: matchedProd, amount: finalScaleQty };
    }

    // 4. Busca por correspondência no nome
    const matchingProducts = products.filter((p) => (
      p.name && p.name.toLowerCase().includes(cleanCode)
    ));
    const matchingServices = services.filter((s) => (
      s.name && s.name.toLowerCase().includes(cleanCode)
    ));

    const totalMatches = matchingProducts.length + matchingServices.length;

    if (totalMatches === 1) {
      if (matchingProducts.length === 1) {
        const item = matchingProducts[0];
        addItem({
          id: item.id,
          name: item.name,
          valueUn: parseNumber(item.value, 0),
          barCode: item.barCode,
          typeItem: 'product',
          category: item.category,
          maxStock: parseNumber(item.amount, 999999),
          images: item.images,
        }, qty);
        return { success: true, item, amount: qty };
      } else {
        const item = matchingServices[0];
        addItem({
          id: item.id,
          name: item.name,
          valueUn: parseNumber(item.value, 0),
          barCode: item.barCode,
          typeItem: 'service',
          category: item.category,
          maxStock: 999999,
          images: item.images,
        }, qty);
        return { success: true, item, amount: qty };
      }
    }

    if (totalMatches > 1) {
      return {
        success: false,
        multipleMatches: true,
        searchTerm: barcodeInput,
        message: `${totalMatches} itens encontrados para '${barcodeInput}'.`,
      };
    }

    return { success: false, message: `Item '${barcodeInput}' não encontrado no catálogo.` };
  }, [productsByBarcode, servicesByBarcode, products, services, addItem]);

  // Atualizar quantidade de um item (preservando precisão decimal e evitando remoções acidentais)
  const updateItemAmount = useCallback((index, newAmount) => {
    setCartItems((prevItems) => {
      const updated = [...prevItems];
      if (!updated[index]) return prevItems;

      const num = parseNumber(newAmount, 0);
      if (num < 0) return prevItems;
      if (num === 0) {
        return updated.filter((_, i) => i !== index);
      }

      updated[index] = calculateLineItem({
        ...updated[index],
        amount: Math.round(num * 10000) / 10000,
      });
      return updated;
    });
  }, []);

  // Atualizar desconto específico de um item
  const updateItemDiscount = useCallback((index, discountVal, discountType = 'money') => {
    setCartItems((prevItems) => {
      const updated = [...prevItems];
      if (!updated[index]) return prevItems;

      updated[index] = calculateLineItem({
        ...updated[index],
        discount: discountType === 'money' ? parseNumber(discountVal, 0) : 0,
        discountPercent: discountType === 'percent' ? parseNumber(discountVal, 0) : 0,
        discountType,
      });
      return updated;
    });
  }, []);

  // Remover item do carrinho
  const removeItem = useCallback((index) => {
    setCartItems((prevItems) => prevItems.filter((_, i) => i !== index));
  }, []);

  // Limpar carrinho
  const clearCart = useCallback(() => {
    setCartItems([]);
    setSelectedCustomer(null);
    setSelectedPromotion(null);
    setAppliedCoupon(null);
    setManualDiscountState(0);
    setManualDiscountType('money');
    setObservation('');
    setPlots(1);
    setSelectedPaymentMethod(null);
  }, []);

  // Avaliação de promoções locais e totais em tempo real
  const promotionEvaluation = useMemo(() => {
    const promoId = selectedPromotion?.id || appliedCoupon?.id || null;
    return evaluatePromotions(cartItems, promotions, promoId, selectedCustomer);
  }, [cartItems, promotions, selectedPromotion, appliedCoupon, selectedCustomer]);

  const totals = useMemo(() => {
    return calculateCartTotals({
      items: promotionEvaluation.items,
      manualDiscount,
      manualDiscountType,
      appliedPromotion: promotionEvaluation.appliedPromotion,
      promoDiscountAmount: promotionEvaluation.discountAmount,
      paymentMethod: selectedPaymentMethod,
      plots,
    });
  }, [
    promotionEvaluation,
    manualDiscount,
    manualDiscountType,
    selectedPaymentMethod,
    plots,
  ]);

  // Validação de cupom sob demanda (online ou no cache de promoções)
  const validateCoupon = useCallback(async (couponCode) => {
    if (!couponCode) return { success: false, message: 'Digite o código do cupom.' };

    const formattedCode = String(couponCode).trim().toUpperCase();

    // Checagem prévia no cache local de promoções
    const localPromo = promotions.find((p) => (
      p.couponCode && String(p.couponCode).trim().toUpperCase() === formattedCode
    ));

    if (localPromo) {
      if (!Boolean(Number(localPromo.isActive ?? 1))) {
        return { success: false, message: `O cupom '${formattedCode}' está inativo.` };
      }
      setAppliedCoupon(localPromo);
      return { success: true, message: `Cupom '${localPromo.name}' aplicado com sucesso!`, promotion: localPromo };
    }

    // Se não encontrou no cache local, consulta na API sob demanda
    try {
      const res = await axios.get(`${API_BASE_URL}/promotions`, {
        headers: authHeaders,
        params: { couponCode: formattedCode },
      });

      if (Array.isArray(res.data) && res.data.length > 0) {
        const found = res.data[0];
        setAppliedCoupon(found);
        return { success: true, message: `Cupom '${found.name}' aplicado!`, promotion: found };
      }
      return { success: false, message: `Cupom '${formattedCode}' não encontrado.` };
    } catch (err) {
      return { success: false, message: 'Não foi possível validar o cupom no servidor.' };
    }
  }, [promotions, authHeaders]);

  const value = {
    // Catálogo e status
    loadingCatalog,
    syncingCatalog,
    catalogError,
    lastSyncTime,
    products,
    services,
    indexedCatalog,
    categories,
    productCategories,
    serviceCategories,
    categoriesMap,
    promotions,
    paymentMethods,
    frequentCustomers,
    syncCatalog,

    // Caixa ativo da sessão
    activeCashier,
    setActiveCashier,
    loadingCashier,
    loadActiveCashier,

    // Carrinho e ações
    cartItems: promotionEvaluation.items,
    totals,
    appliedPromotion: promotionEvaluation.appliedPromotion,
    appliedCoupon,
    selectedCustomer,
    setSelectedCustomer,
    selectedPromotion,
    setSelectedPromotion,
    manualDiscount,
    setManualDiscount: setManualDiscountState,
    manualDiscountType,
    setManualDiscountType,
    observation,
    setObservation,
    plots,
    setPlots,
    selectedPaymentMethod,
    setSelectedPaymentMethod,

    addItemByBarcode,
    addItem,
    updateItemAmount,
    updateItemDiscount,
    removeItem,
    clearCart,
    validateCoupon,

    // Fullscreen
    isFullScreen,
    toggleFullScreen,

    // Tema (Modo Escuro / Claro)
    themeMode,
    isDarkMode,
    toggleTheme,
  };

  return (
    <POSContext.Provider value={value}>
      {children}
    </POSContext.Provider>
  );
};

export const usePOS = () => useContext(POSContext);
export default POSContext;
